import Storage from "../utils/Storage";
import { MCP_AUTHORIZE_PATH } from "../utils/constants";

export const grantAccess = (token, user, redirectTo, selectedCompanyUuid) => {
  const search = window.location.search;
  const params = new URLSearchParams(search);
  const redirect_uri = params?.get("redirect_uri");

  // ROAR-4902: wipe any storage left over from a prior session (e.g. an abandoned signup that
  // got as far as setting a token + encrypting company_id) before establishing this one — company_id
  // is encrypted under a key derived from whatever token is currently in Storage, so leaving a
  // stale blob in place makes it undecryptable ("Malformed UTF-8 data") the instant this new
  // token replaces the old one, crashing the first unguarded decrypt (no error boundary catches it).
  // Also clears sessionStorage (mirrors logout()) — GetStartedGate's one-shot redirect flag lives
  // there, and without this a brand-new account in a tab previously used for another session
  // inherits that flag already set, silently skipping its own Get Started redirect.
  Storage.clear();
  Storage.clear(true);
  Storage.setToken(token);

  // Password/SSO login responses return the most-recently-created company a provider-tier
  // (siemba_admin/siemba_tester) or super_admin account has access to, as a sensible pre-selected
  // default before the user has picked anything from the company switcher. Stored the same way
  // GlobalCompanySelection persists an explicit pick, minus the full customer record (name,
  // feature flags) it doesn't have yet — GlobalCompanySelection upgrades this once
  // list_my_customers loads. `null` means zero accessible companies — same as an empty
  // list_my_customers response, so nothing to store; `undefined` means the field wasn't in this
  // response at all (e.g. a customer-tier login), so leave any existing selection untouched.
  if (selectedCompanyUuid) {
    Storage.encryptStorageData('company_id', JSON.stringify({ uuid: selectedCompanyUuid }));
  }

  if (redirect_uri && validateURL(redirect_uri)) {
    window.location = redirect_uri;
  } else if (redirectTo && validateURL(redirectTo)) {
    window.location = redirectTo;
  } else if (user.is_superadmin) {
    window.location = "/admin";
  } else {
    window.location = "/profile";
  }
};


function validateURL(sUrl) {
    try {
      // ROAR-4988: compare the full origin (scheme + host + port), not just the hostname. A
      // `javascript:`/`data:` URL has an empty hostname and used to pass a hostname-only check,
      // making ?redirect_uri an XSS sink; its origin is the opaque "null", so it fails here. A
      // same-host URL on another port or scheme is a different origin and is rejected too.
      return new URL(sUrl, window.location.origin).origin === window.location.origin;
    } catch {
      return false;
    }
  }

const MAX_RELATIVE_PATH_LENGTH = 2048;
// eslint-disable-next-line no-control-regex
const UNSAFE_PATH_CHARS = /[\\\u0000-\u001f\u007f]/;

const parseSameOriginUrl = (value) => {
  try {
    const url = value.startsWith('/') ? new URL(value, window.location.origin) : new URL(value);
    return url.origin === window.location.origin ? url : null;
  } catch {
    return null;
  }
};

// A `/\` prefix is rejected too: backslash is in UNSAFE_PATH_CHARS.
const isSafeRelativePath = (path) =>
  path.startsWith('/') &&
  !path.startsWith('//') &&
  !UNSAFE_PATH_CHARS.test(path) &&
  path.length <= MAX_RELATIVE_PATH_LENGTH;

/**
 * ROAR-4988 (plan §4 decision 7) — reduces a relative path or a same-origin absolute URL to
 * `pathname + search + hash`, or returns null. Mirrors the backend BE3 `returnTo` rules: starts
 * with `/`, not `//` or `/\`, no backslash or control characters, at most 2048 characters.
 * Control characters are checked on the raw input too, because the URL parser silently strips
 * tabs and newlines.
 *
 * @param {*} value Candidate path, typically the login screen's `?redirect_uri`.
 * @returns {string|null} A safe same-origin relative path, or null.
 */
export const toSafeRelativePath = (value) => {
  if (typeof value !== 'string' || value === '' || UNSAFE_PATH_CHARS.test(value)) {
    return null;
  }
  const url = parseSameOriginUrl(value);
  if (!url) {
    return null;
  }
  const path = `${url.pathname}${url.search}${url.hash}`;
  return isSafeRelativePath(path) ? path : null;
};

/**
 * ROAR-4988 (decision 2) — true on the headless MCP handoff route, with or without a trailing slash.
 *
 * @param {string} pathname
 * @returns {boolean}
 */
export const isMcpAuthorizePath = (pathname) => {
  if (typeof pathname !== 'string') {
    return false;
  }
  const normalized = pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
  return normalized === MCP_AUTHORIZE_PATH;
};

/**
 * ROAR-4988 (decision 2) — the login URL that brings the user back to the current page after
 * sign-in. Read at call time, and the single builder for every caller (logout() on the MCP path,
 * withPermissions) so the encoding is identical everywhere (one encoding layer, decoded once by
 * grantAccess's URLSearchParams). Keeps the hash so deep links like `#section` survive login.
 *
 * @returns {string}
 */
export const buildLoginRedirect = () =>
  '/?redirect_uri=' +
  encodeURIComponent(window.location.pathname + window.location.search + (window.location.hash || ''));

export const logout = () => {
    Storage.clear()
    //clear session storage
    Storage.clear(true)
    // ROAR-4988 (decision 3): on /mcp/authorize a 401/440 must land on login with the handoff
    // params preserved, and `replace` keeps the expired page out of history.
    if (isMcpAuthorizePath(window.location.pathname)) {
        window.location.replace(buildLoginRedirect())
        return
    }
    window.location = "/"
}
